import { ExerciseTypeNames, ExerciseType, Skill } from "@tt-app/models";

import iconMicrophone from "@joyz/app-core/assets/icons/default/Microphone.svg";
import iconHeadphone from "@joyz/app-core/assets/icons/default/Headphone.svg";
import iconDrillListening from "@joyz/app-core/assets/icons/default/DrillListening.svg";
import iconDrillPronunciation from "@joyz/app-core/assets/icons/default/DrillPronunciation.svg";
import iconDrillTyping from "@joyz/app-core/assets/icons/default/DrillTyping.svg";
import iconDrillVocabulary from "@joyz/app-core/assets/icons/default/DrillVocabulary.svg";
import iconSelection from "@joyz/app-core/assets/icons/default/Selection.svg";
import iconBook from "@joyz/app-core/assets/icons/default/Book.svg";
import iconWriting from "@joyz/app-core/assets/icons/default/Writing.svg";

const CHAT = 1;
const DEMO_LISTENING = 2;
const DICTATION = 3;
const DRILL_VOCABULARY = 4;
const DRILL_LISTENING = 5;
const DRILL_PRONUNCIATION = 6;
const QUIZ_LISTENING = 7;
const PICTURE_MATCH = 8;
const DRILL_TYPING = 9;
const SINGLE_SENTENCE_READING = 10;
const QUIZ_TRANSLATION = 11;
const DRILL_TRANSLATION = 12;
const WRITING_EXERCISE = 13;

export const EXERCISE_TYPE: { [key in ExerciseTypeNames]: ExerciseType } = {
  CHAT: CHAT,
  DEMO_LISTENING: DEMO_LISTENING,
  DICTATION: DICTATION,
  DRILL_VOCABULARY: DRILL_VOCABULARY,
  DRILL_LISTENING: DRILL_LISTENING,
  DRILL_PRONUNCIATION: DRILL_PRONUNCIATION,
  QUIZ_LISTENING: QUIZ_LISTENING,
  PICTURE_MATCH: PICTURE_MATCH,
  DRILL_TYPING: DRILL_TYPING,
  SINGLE_SENTENCE_READING: SINGLE_SENTENCE_READING,
  QUIZ_TRANSLATION: QUIZ_TRANSLATION,
  DRILL_TRANSLATION: DRILL_TRANSLATION,
  WRITING: WRITING_EXERCISE
};

export const NON_TROPHY_EXERCISES = [EXERCISE_TYPE.DEMO_LISTENING, EXERCISE_TYPE.WRITING];

/**
Exercises which have been adapted to use generic structure found in @tt-app/components/exercise/consolidated
**/
export const CONSOLIDATED_EXERCISE_TYPES: number[] = [EXERCISE_TYPE.WRITING];

/**
 Mapping of ExerciseTypes to their appropriate display icons.
 Does not compute from EXERCISE_TYPE due to a typescript bug @ microsoft/TypeScript/issues/13948
 Used as a fallback for legacy exercises and assignments that do not have associated skills.
**/
export const EXERCISE_ICONS: Record<ExerciseType, string> = {
  [CHAT]: iconMicrophone,
  [DEMO_LISTENING]: iconHeadphone,
  [DICTATION]: iconHeadphone,
  [DRILL_VOCABULARY]: iconDrillVocabulary,
  [DRILL_LISTENING]: iconDrillListening,
  [DRILL_PRONUNCIATION]: iconDrillPronunciation,
  [QUIZ_LISTENING]: iconHeadphone,
  [PICTURE_MATCH]: iconSelection,
  [DRILL_TYPING]: iconDrillTyping,
  [SINGLE_SENTENCE_READING]: iconMicrophone,
  [QUIZ_TRANSLATION]: iconSelection,
  [DRILL_TRANSLATION]: iconDrillTyping,
  [WRITING_EXERCISE]: iconDrillTyping
};

/**
Configuration of each exercise type.
Specifies what skills each exercise type supports, and what icon each skill uses.
**/

const SKILL_SPEAKING = 1;
const SKILL_LISTENING = 2;
const SKILL_READING = 3;
const SKILL_WRITING = 4;

interface ExerciseConfig {
  icon: string;
}

export const DRILL_DEFAULT_CONFIG: Record<Skill, ExerciseConfig> = {
  [SKILL_SPEAKING]: { icon: iconDrillPronunciation },
  [SKILL_LISTENING]: { icon: iconDrillListening },
  [SKILL_READING]: { icon: iconDrillVocabulary },
  [SKILL_WRITING]: { icon: iconDrillTyping }
};

export const EXERCISE_DEFAULT_CONFIG: Record<Skill, ExerciseConfig> = {
  [SKILL_SPEAKING]: { icon: iconMicrophone },
  [SKILL_LISTENING]: { icon: iconHeadphone },
  [SKILL_READING]: { icon: iconBook },
  [SKILL_WRITING]: { icon: iconWriting }
};

export const EXERCISE_CONFIGS: Record<ExerciseType, Record<Skill, ExerciseConfig>> = {
  [CHAT]: EXERCISE_DEFAULT_CONFIG,
  [DEMO_LISTENING]: EXERCISE_DEFAULT_CONFIG,
  [DICTATION]: EXERCISE_DEFAULT_CONFIG,
  [DRILL_VOCABULARY]: DRILL_DEFAULT_CONFIG,
  [DRILL_LISTENING]: DRILL_DEFAULT_CONFIG,
  [DRILL_PRONUNCIATION]: DRILL_DEFAULT_CONFIG,
  [QUIZ_LISTENING]: EXERCISE_DEFAULT_CONFIG,
  [PICTURE_MATCH]: EXERCISE_DEFAULT_CONFIG,
  [DRILL_TYPING]: DRILL_DEFAULT_CONFIG,
  [SINGLE_SENTENCE_READING]: EXERCISE_DEFAULT_CONFIG,
  [QUIZ_TRANSLATION]: EXERCISE_DEFAULT_CONFIG,
  [DRILL_TRANSLATION]: DRILL_DEFAULT_CONFIG,
  [WRITING_EXERCISE]: EXERCISE_DEFAULT_CONFIG
};

export const SKILL_TITLE: Record<Skill, string> = {
  [SKILL_SPEAKING]: "skills.name.speaking",
  [SKILL_LISTENING]: "skills.name.listening",
  [SKILL_READING]: "skills.name.reading",
  [SKILL_WRITING]: "skills.name.writing"
};
